import { type FC, type PropsWithChildren, useState } from "react";
import { Modal as BsModal, Form } from "react-bootstrap";
import type { ModalProps } from "react-bootstrap/Modal";
import { useEventCallback } from "usehooks-ts";
import { Button, type ButtonProps } from "../../components/Button";
import { ButtonIcon } from "../../components/ButtonIcon";
import { AlertMessage } from "../alert/AlertMessage";
import { useModalContext } from "./ModalContext";

interface BaseModalProps extends ModalProps {
	onCancel?: () => unknown;
}

interface PortalModalProps extends BaseModalProps, PropsWithChildren {
	backdropDismiss?: boolean;
	escapeDismiss?: boolean;
}

const Modal: FC<PortalModalProps> = (props) => {
	const { close } = useModal();
	const {
		backdropDismiss = false,
		escapeDismiss = true,
		children,
		onCancel = close,
		...rest
	} = props;

	return (
		<BsModal
			show
			onHide={onCancel}
			backdrop={backdropDismiss ? true : "static"}
			keyboard={escapeDismiss}
			{...rest}
			data-testid="modal"
		>
			<BsModal.Body>
				<ButtonIcon
					icon="close"
					className="modal-close"
					onClick={onCancel}
					aria-label="Close dialog"
				>
					Close
				</ButtonIcon>
				<AlertMessage />
				{children}
			</BsModal.Body>
		</BsModal>
	);
};

interface ModalConfirmProps extends BaseModalProps, PropsWithChildren {
	onConfirm?: () => Promise<void>;
	variant?: ButtonProps["variant"];
	confirmText?: string;
}

const ModalConfirm: FC<ModalConfirmProps> = (props) => {
	const {
		onConfirm,
		variant = "primary",
		children,
		confirmText = "Confirm",
		...rest
	} = props;
	const { close } = useModal();

	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = useEventCallback((e: React.FormEvent) => {
		e.preventDefault();
		if (onConfirm === undefined) {
			return;
		}

		setSubmitting(true);
		return onConfirm()
			.then((x) => {
				close();

				return x;
			})
			.finally(() => setSubmitting(false));
	});

	return (
		<Modal {...rest}>
			<Form>
				{children}

				<div className="mt-3">
					<Button
						loading={submitting}
						variant={variant}
						full
						onClick={handleSubmit}
					>
						{confirmText}
					</Button>
				</div>
			</Form>
		</Modal>
	);
};

const ModalNamespace = Object.assign(Modal, {
	Confirm: ModalConfirm,
});

export const useModal = () => {
	const { open, close } = useModalContext();

	const confirm = (props: ModalConfirmProps) => open(ModalConfirm, props);

	return { confirm, open, close };
};

export { ModalNamespace as Modal };
